import {
  ArrowDown01Icon,
  CancelCircleIcon,
  CheckmarkCircle01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Portal } from 'react-native-paper';
import { Colors } from '../constants/Colors';
import { purple1Shadow } from '../constants/Styles';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import { isIos, type WithResponsive } from '../utils/responsivity';
import { Button } from './Button';
import { Loader } from './Loader';
import { TextReg } from './StyledText';

type Props<T extends string | number> = {
  label?: string;
  placeholder?: string;
  options: Array<{ label: string; value: T }>;
  values?: T[];
  onChange: (values: T[]) => void;
  buttonStyle?: StyleProp<ViewStyle>;
  menuStyle?: StyleProp<ViewStyle>;
  notClearable?: boolean;
  variant?: 'dense' | 'normal';
  showLoadingState?: boolean;
  error?: boolean;
};

export function MultiDropdown<T extends string | number>({
  label,
  placeholder,
  options,
  values = [],
  onChange,
  buttonStyle,
  notClearable,
  variant = 'dense',
  menuStyle,
  showLoadingState,
  error,
}: Props<T>) {
  const styles = useDynamicStyles(createStyles<T>, { variant });
  const dropdownRef = useRef<View>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [menuPosition, setMenuPosition] = useState<{
    top?: number;
    left: number;
    width: number;
    bottom?: number;
  }>({
    left: 0,
    width: 0,
  });

  // Custom dropdown using Portal for more control
  const [customMenuVisible, setCustomMenuVisible] = useState(false);

  const openMenu = () => {
    if (dropdownRef.current) {
      // Retries needed because measureInWindow can return invalid values on Android
      // if the view hasn't fully laid out yet. Retrying gives the layout time to complete.
      // See: https://github.com/facebook/react-native/issues/18034
      const attemptOpen = (retries: number) => {
        dropdownRef.current?.measureInWindow((x, y, width, height) => {
          const hasValidMeasurements =
            width > 0 && height > 0 && !isNaN(x) && !isNaN(y);

          if (hasValidMeasurements) {
            const screenHeight = Dimensions.get('window').height;
            const menuMaxHeight = 300;
            const spaceBelow = screenHeight - (y + height);
            const spaceAbove = y;

            const shouldPositionAbove =
              spaceBelow < menuMaxHeight && spaceAbove > spaceBelow;

            setMenuPosition({
              top: shouldPositionAbove ? undefined : y + height,
              left: x,
              width: width,
              bottom: shouldPositionAbove ? screenHeight - y : undefined,
            });
            setCustomMenuVisible(true);
          } else if (retries > 0) {
            timeoutRef.current = setTimeout(
              () => attemptOpen(retries - 1),
              100,
            );
          } else {
            // Fallback: open with screen-centered positioning
            const screenWidth = Dimensions.get('window').width;
            const screenHeight = Dimensions.get('window').height;
            setMenuPosition({
              top: screenHeight / 3,
              left: 20,
              width: screenWidth - 40,
            });
            setCustomMenuVisible(true);
          }
        });
      };
      attemptOpen(3);
    }
  };

  // Cleanup timeout on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const closeMenu = () => {
    setCustomMenuVisible(false);
  };

  const toggleItem = (value: T) => {
    const newValues = values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];
    onChange(newValues);
    // Menu stays open because we're using our custom menu
  };

  const clearValues = () => {
    onChange([]);
    closeMenu();
  };

  const selectedLabels = options
    .filter((option) => values.includes(option.value))
    .map((option) => option.label);

  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  }, []);

  // Create display text based on selection - show comma-separated labels with smart truncation
  const displayText = useMemo(() => {
    if (selectedLabels.length === 0) {
      return placeholder;
    }

    // For a single selection, just show the label
    if (selectedLabels.length === 1) {
      return selectedLabels[0];
    }

    // Calculate available width for text (container minus padding and icon space)
    const paddingAndIcon = 72; // 16px left padding + 16px right padding + ~40px for icon
    const availableWidth = Math.max(containerWidth - paddingAndIcon, 100);

    // Approximate character width (Poppins 16px is roughly 8-9px per char on average)
    const avgCharWidth = 8.5;
    const maxChars = Math.floor(availableWidth / avgCharWidth);

    // For multiple selections, build comma-separated list with truncation
    let result = '';
    let displayedCount = 0;

    for (let i = 0; i < selectedLabels.length; i++) {
      const label = selectedLabels[i];
      const separator = displayedCount > 0 ? ', ' : '';
      const potentialText = result + separator + label;
      const remaining = selectedLabels.length - displayedCount - 1;

      // Calculate what the "and X more" suffix would be
      const suffix = remaining > 0 ? `, and ${remaining} more` : '';

      // Check if adding this label (plus potential suffix) would exceed available space
      if (
        potentialText.length + suffix.length > maxChars &&
        displayedCount > 0
      ) {
        const actualRemaining = selectedLabels.length - displayedCount;
        return `${result}, and ${actualRemaining} more`;
      }

      result = potentialText;
      displayedCount++;
    }

    return result;
  }, [selectedLabels, containerWidth, placeholder]);

  const canClearSelection = selectedLabels.length > 0 && !notClearable;

  const dropdownContent = (
    <View style={menuStyle} ref={dropdownRef} onLayout={handleContainerLayout}>
      <Button
        mode="contained"
        onPress={options.length ? openMenu : undefined}
        style={[buttonStyle, error && styles.errorBorder]}
        labelStyle={styles.buttonLabel}
        buttonColor={isIos ? Colors.opacity025 : Colors.opacity05}
        size={variant === 'dense' ? 'medium' : 'large'}
        disabled={options.length == 0 && showLoadingState}
      >
        <TextReg style={styles.hiddenText}>{displayText}</TextReg>
      </Button>

      <View style={styles.absoluteContent}>
        {selectedLabels.length > 0 ? (
          <TextReg numberOfLines={1} style={styles.text}>
            {displayText}
          </TextReg>
        ) : (
          <TextReg numberOfLines={1} style={styles.placeholder}>
            {placeholder}
          </TextReg>
        )}
        {options.length ? (
          canClearSelection ? (
            <TouchableOpacity onPress={clearValues} style={styles.clearButton}>
              <HugeiconsIcon icon={CancelCircleIcon} color={Colors.text} />
            </TouchableOpacity>
          ) : (
            <HugeiconsIcon icon={ArrowDown01Icon} color={Colors.text} />
          )
        ) : (
          showLoadingState && <Loader style={styles.loader} />
        )}
      </View>

      {/* Custom menu using Portal for more control */}
      {customMenuVisible && (
        <Portal>
          <TouchableOpacity style={styles.backdrop} onPress={closeMenu}>
            <View
              style={[
                styles.customMenuContainer,
                {
                  position: 'absolute',
                  top: menuPosition.top,
                  bottom: menuPosition.bottom,
                  left: menuPosition.left,
                  width: menuPosition.width,
                },
              ]}
            >
              <View style={styles.customMenu}>
                <ScrollView
                  style={styles.scrollContainer}
                  showsVerticalScrollIndicator={true}
                  nestedScrollEnabled={true}
                >
                  {options.map((option) => {
                    const isSelected = values.includes(option.value);
                    return (
                      <TouchableOpacity
                        key={option.value.toString()}
                        onPress={() => toggleItem(option.value)}
                        style={styles.menuItem}
                      >
                        <View style={styles.menuItemContent}>
                          <TextReg
                            style={isSelected ? styles.selectedText : undefined}
                          >
                            {option.label}
                          </TextReg>
                          {isSelected && (
                            <HugeiconsIcon
                              icon={CheckmarkCircle01Icon}
                              color={Colors.purple1}
                              size={18}
                            />
                          )}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
                <View style={styles.doneButtonContainer}>
                  <Button mode="contained" onPress={closeMenu} size="small">
                    Done
                  </Button>
                </View>
              </View>
            </View>
          </TouchableOpacity>
        </Portal>
      )}
    </View>
  );

  if (label) {
    return (
      <View style={labelContainerStyles.container}>
        <TextReg>{label}</TextReg>
        {dropdownContent}
      </View>
    );
  }

  return dropdownContent;
}

const labelContainerStyles = StyleSheet.create({
  container: {
    gap: 4,
  },
});

function createStyles<T extends string | number>({
  variant,
}: WithResponsive<Props<T>>) {
  return StyleSheet.create({
    buttonLabel: {
      paddingRight: 16,
    },
    absoluteContent: {
      position: 'absolute',
      top: 0,
      backgroundColor: 'transparent',
      pointerEvents: 'none',
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingLeft: 16,
      paddingRight: 16,
      height: variant === 'dense' ? 40 : 56,
      width: '100%',
      gap: 10,
    },
    content: {
      height: variant === 'dense' ? 40 : 56,
    },
    label: {
      width: '100%',
      marginLeft: 16,
      marginRight: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: 16,
    },
    text: {
      fontSize: 16,
    },
    placeholder: {
      fontSize: 16,
      color: Colors.placeholderText,
    },
    hiddenText: {
      fontSize: 16,
      color: 'transparent',
    },
    backdrop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
      zIndex: 1000,
    },
    customMenuContainer: {
      backgroundColor: 'transparent',
      borderRadius: 8,
      maxHeight: 300,
      zIndex: 1001,
      ...purple1Shadow,
    },
    customMenu: {
      backgroundColor: Colors.purple0,
      borderRadius: 8,
      overflow: 'hidden',
    },
    scrollContainer: {
      maxHeight: 250, // Leave room for the Done button
    },
    menuItem: {
      padding: 10,
      paddingHorizontal: 16,
      minHeight: 48,
      justifyContent: 'center',
    },
    menuItemContent: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      cursor: 'pointer',
    },
    doneButtonContainer: {
      flexDirection: 'row',
      padding: 12,
      paddingHorizontal: 16,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: Colors.opacity20,
    },
    clearButton: {
      pointerEvents: 'auto',
      padding: 4,
      alignItems: 'center',
      justifyContent: 'center',
    },
    loader: {
      alignItems: 'flex-end',
    },
    selectedText: {
      color: Colors.purple1,
    },
    errorBorder: {
      borderWidth: 1,
      borderColor: Colors.orange0,
    },
  });
}
